'use client';

import { ScrollArea } from '@/components/common/layout/scroll-area';
import { ChatGroup } from '@/components/features/chat/components/sidebar/ChatGroup';
import { sidebarVariants } from '@/lib/helpers/animations/variants';
import { useAuth } from '@/lib/hooks/useAuth';
import { useToast } from '@/lib/hooks/useToast';
import { adviceService } from '@/lib/services';
import { adviceActions, adviceState } from '@/lib/store/advice.store';
import { sidebarActions, sidebarState } from '@/lib/store/sidebar.store';
import { cn } from '@/lib/utils';
import { groupChatsByDate } from '@/lib/utils/chat-utils';
import { useI18n } from '@/locales/client';
import { ConversationListItem } from '@/types';
import { batch } from '@legendapp/state';
import { observer, useComputed } from '@legendapp/state/react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import type { Session } from 'next-auth';
import { usePathname, useRouter } from 'next/navigation';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// Types
type TimePeriod = 'today' | 'yesterday' | 'lastWeek' | 'lastMonth';

type GroupedChats = {
  [key in TimePeriod]?: ConversationListItem[];
};

interface SidebarContextType {
  state: {
    status: string;
    error: Error | null;
    isTransitioning: boolean;
    isDeleting: boolean;
    currentChatId: string | null;
    isPending: boolean;
    isLoading: boolean;
    isChatLoading: boolean;
    isStreaming: boolean;
  };
  actions: {
    handleChatClick: (id: string) => Promise<void>;
    handleNewChatClick: () => Promise<void>;
    handleDelete: (id: string) => Promise<void>;
    handleEdit: (e: any, id: string) => Promise<void>;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setCurrentEditChat: (id: string | null) => void;
    setEditValue: (value: string | null) => void;
  };
  ui: {
    currentEditChat: string | null;
    editValue: string | null;
    editInputRef: React.RefObject<HTMLInputElement>;
    pathname: string | null;
    t: ReturnType<typeof useI18n>;
    toast: ReturnType<typeof useToast>['toast'];
    session: Session | null;
  };
  data: {
    groupedChats: GroupedChats;
    conversationsState: typeof adviceState.conversations;
  };
}

// Translation mapping
const TIME_TRANSLATIONS: Record<TimePeriod, `question.${TimePeriod}`> = {
  today: 'question.today',
  yesterday: 'question.yesterday',
  lastWeek: 'question.lastWeek',
  lastMonth: 'question.lastMonth',
};

// Create context for sidebar state and actions
const SidebarContext = createContext<SidebarContextType | null>(null);

// Custom hook for using sidebar context
const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

// Component for rendering the "New Chat" button
const NewChatButton = () => {
  const { state, actions, ui } = useSidebar();
  const { isPending, isTransitioning } = state;
  const { handleNewChatClick } = actions;
  const { t } = ui;

  return (
    <div className='flex-shrink-0 bg-muted p-2 shadow-sm'>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleNewChatClick}
        className={cn(
          'flex w-full items-center rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground shadow-md hover:bg-muted/50',
          (isPending || isTransitioning) && 'cursor-not-allowed opacity-50'
        )}
        disabled={isPending || isTransitioning}
        data-testid='new-chat-button'
      >
        <PlusIcon className='mr-2 h-4 w-4' />
        {t('question.new')}
      </motion.button>
    </div>
  );
};

// Component for rendering chat groups
const ChatGroupList = () => {
  const { state, actions, ui, data } = useSidebar();
  const { groupedChats } = data;
  const { t } = ui;

  return (
    <ScrollArea className='flex-1 touch-auto overflow-auto'>
      <AnimatePresence mode='popLayout'>
        <motion.div layout className='space-y-2'>
          {Object.entries(groupedChats).map(([key, chats]) =>
            chats && chats.length > 0 ? (
              <ChatGroup
                key={key}
                title={t(TIME_TRANSLATIONS[key as TimePeriod])}
                chats={chats}
                pathname={ui.pathname || ''}
                currentChat={ui.currentEditChat}
                setCurrentChat={actions.setCurrentEditChat}
                setCurrentChatValue={actions.setEditValue}
                handleEdit={actions.handleEdit}
                handleDelete={actions.handleDelete}
                currentChatRef={ui.editInputRef}
                handleInputChange={actions.handleInputChange}
                currentChatValue={ui.editValue}
                onChatClick={actions.handleChatClick}
                onNavigate={actions.handleChatClick}
                isPending={state.isPending}
                isTransitioning={state.isTransitioning}
                currentChatId={state.currentChatId}
                toast={ui.toast}
                session={ui.session}
                conversationsState={data.conversationsState}
              />
            ) : null
          )}
        </motion.div>
      </AnimatePresence>
    </ScrollArea>
  );
};

// Main content component with access to context
const ChatContent = () => {
  return (
    <motion.div
      variants={sidebarVariants}
      initial='hidden'
      animate='visible'
      exit='exit'
      className='flex h-full w-full flex-col overflow-hidden border-r bg-white dark:bg-gray-900'
      data-testid='chat-content'
    >
      <NewChatButton />
      <ChatGroupList />
    </motion.div>
  );
};

const useChatSidebar = () => {
  const router = useRouter();
  const pathname = usePathname();
  const t = useI18n();
  const { toast } = useToast();
  const { session } = useAuth();
  const [currentEditChat, setCurrentEditChat] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const [editValue, setEditValue] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const lastNavigationRef = useRef<string | null>(null);

  // Get conversations from store
  const conversations = adviceState.conversations;

  // Compute grouped chats reactively
  const groupedChats = useComputed(() => {
    const items = conversations.get().items || [];
    if (!items.length) {
      return {
        today: [],
        yesterday: [],
        lastWeek: [],
        lastMonth: [],
      };
    }
    return groupChatsByDate(items as unknown as ConversationListItem[]);
  });

  // Subscribe to sidebar state
  const sidebarLocalState = useComputed(() => ({
    status: sidebarState.status.get(),
    error: sidebarState.error.get(),
    isTransitioning: sidebarState.isTransitioning.get(),
    isDeleting: sidebarState.isDeleting.get(),
    currentChatId: adviceState.currentChatId.get(),
    isPending: sidebarState.status.get() === 'pending',
    isLoading: sidebarState.status.get() === 'loading',
    isChatLoading: adviceState.isChatLoading?.get() || false,
    isStreaming: adviceState.isStreaming?.get() || false,
  }));

  // Fetch conversations on mount if session exists
  useEffect(() => {
    if (session?.user?.id) {
      adviceActions.setSession(session);
      adviceActions.fetchConversations();
    }
  }, [session]);

  // Set mounted state on first render
  useEffect(() => {
    setMounted(true);
  }, []);

  // Chat navigation handler
  const handleChatClick = useCallback(
    async (chatId: string) => {
      const state = sidebarLocalState.get();
      if (
        state.status === 'pending' ||
        state.isTransitioning ||
        state.isChatLoading ||
        state.isStreaming
      ) {
        return;
      }
      try {
        const navigationPath = `?chatId=${chatId}`;
        if (lastNavigationRef.current === navigationPath) return;
        batch(() => {
          adviceActions.startTransition();
          lastNavigationRef.current = navigationPath;
        });
        if (session) {
          const conversation = await adviceService.getById(chatId, session);
          batch(() => {
            adviceActions.setActiveConversation(conversation);
            adviceActions.setCurrentChatId(chatId);
            adviceActions.completeTransition();

            // Handle mobile responsiveness
            if (typeof window !== 'undefined' && window.innerWidth < 768) {
              sidebarActions.closeSidebar();
            }
          });
        }
      } catch (error) {
        batch(() => {
          adviceActions.failRequest(error as Error);
          toast({
            variant: 'destructive',
            description: 'Failed to load conversation',
          });
        });
      }
    },
    [session, toast, sidebarLocalState]
  );

  // New chat handler
  const handleNewChatClick = useCallback(async () => {
    try {
      const controller = adviceState.activeController.get();
      if (controller instanceof AbortController) {
        controller.abort('RESET_CHAT');
        adviceActions.setActiveController(null);
      }
      batch(() => {
        adviceState.status.set('ready');
        adviceState.error.set(null);
        adviceState.isTransitioning.set(false);
        sidebarActions.resetSidebarState();
        adviceState.activeConversation.set(null);
        adviceState.advices.set([]);
        adviceState.currentChatId.set(null);
        adviceState.isChatLoading.set(false);
        adviceState.isStreaming.set(false);
        router.replace('/', { scroll: false });
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        description: t('chat.error'),
      });
    }
  }, [router, t, toast]);

  // Delete chat handler
  const handleDelete = async (id: string) => {
    try {
      await adviceActions.deleteChat(id);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // Edit chat handler
  const handleEdit = async (e: any, id: string) => {
    if (e.key === 'Escape') {
      setCurrentEditChat(null);
      setEditValue(null);
      return;
    }
    const title = editValue?.trim();
    if (!title) return;
    try {
      await adviceActions.updateTitle(id, title);
      setCurrentEditChat(null);
      setEditValue(null);
    } catch (error) {
      console.error('Edit error:', error);
    }
  };

  // Input change handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setEditValue(e.target.value);
    },
    []
  );

  return {
    state: sidebarLocalState.get(),
    actions: {
      handleChatClick,
      handleNewChatClick,
      handleDelete,
      handleEdit,
      handleInputChange,
      setCurrentEditChat,
      setEditValue,
    },
    ui: {
      currentEditChat,
      editValue,
      editInputRef,
      pathname,
      t,
      toast,
      session,
    },
    data: {
      groupedChats: groupedChats.get(),
      conversationsState: conversations,
    },
    mounted,
  };
};

// Main component definition
export const ChatListSidebar = observer(() => {
  useEffect(() => {
    sidebarActions.initializeSidebar();
  }, []);

  const { state, actions, ui, data, mounted } = useChatSidebar();

  if (!mounted) {
    return null;
  }

  return (
    <SidebarContext.Provider value={{ state, actions, ui, data }}>
      <ChatContent />
    </SidebarContext.Provider>
  );
});
